import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const ThemeCustomizer = () => {
  const [selectedColor, setSelectedColor] = useState('#b30101');
  const [customColor, setCustomColor] = useState('');

  const presetColors = [
    { name: 'Red', primary: '#b30101', secondary: '#8b0000', accent: '#dc143c' },
    { name: 'Blue', primary: '#2563eb', secondary: '#1d4ed8', accent: '#3b82f6' },
    { name: 'Orange', primary: '#ea580c', secondary: '#c2410c', accent: '#f97316' },
    { name: 'Yellow', primary: '#eab308', secondary: '#ca8a04', accent: '#facc15' },
    { name: 'Green', primary: '#16a34a', secondary: '#15803d', accent: '#22c55e' },
    { name: 'Purple', primary: '#9333ea', secondary: '#7e22ce', accent: '#a855f7' },
    { name: 'Pink', primary: '#db2777', secondary: '#be185d', accent: '#ec4899' },
    { name: 'Teal', primary: '#0d9488', secondary: '#0f766e', accent: '#14b8a6' },
    { name: 'Indigo', primary: '#4f46e5', secondary: '#4338ca', accent: '#6366f1' },
    { name: 'Brown', primary: '#92400e', secondary: '#78350f', accent: '#b45309' },
    { name: 'Grey', primary: '#4b5563', secondary: '#374151', accent: '#6b7280' },
    { name: 'Black', primary: '#1a1a1a', secondary: '#000000', accent: '#404040' }
  ];

  useEffect(() => {
    // Load saved theme from localStorage
    const savedTheme = localStorage.getItem('siteTheme');
    if (savedTheme) {
      const theme = JSON.parse(savedTheme);
      setSelectedColor(theme.primary);
      applyTheme(theme.primary, theme.secondary, theme.accent);
    }
  }, []);

  const lightenColor = (color, percent) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16).slice(1);
  };

  const darkenColor = (color, percent) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return '#' + (0x1000000 + (R > 0 ? R : 0) * 0x10000 +
      (G > 0 ? G : 0) * 0x100 +
      (B > 0 ? B : 0))
      .toString(16).slice(1);
  };

  const applyTheme = (primary, secondary, accent) => {
    const root = document.documentElement;
    
    // Calculate light versions
    const lightColor = lightenColor(primary, 85);
    const veryLightColor = lightenColor(primary, 95);
    
    // Convert hex to RGB for rgba usage
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
    };
    
    const primaryRgb = hexToRgb(primary);
    const secondaryRgb = hexToRgb(secondary);
    const accentRgb = hexToRgb(accent);
    
    // Apply CSS variables
    root.style.setProperty('--primary-red', primary);
    root.style.setProperty('--secondary-red', secondary);
    root.style.setProperty('--accent-red', accent);
    root.style.setProperty('--light-red', lightColor);
    root.style.setProperty('--very-light-red', veryLightColor);
    
    // Set RGB values for rgba usage
    root.style.setProperty('--primary-red-rgb', primaryRgb);
    root.style.setProperty('--secondary-red-rgb', secondaryRgb);
    root.style.setProperty('--accent-red-rgb', accentRgb);
    
    // Also update blue and gold variables (they're aliased to red now)
    root.style.setProperty('--primary-blue', primary);
    root.style.setProperty('--secondary-blue', secondary);
    root.style.setProperty('--accent-blue', accent);
    root.style.setProperty('--light-blue', lightColor);
    root.style.setProperty('--very-light-blue', veryLightColor);
    root.style.setProperty('--primary-gold', primary);
    root.style.setProperty('--secondary-gold', secondary);
    root.style.setProperty('--light-gold', lightColor);
  };

  const handlePresetColorChange = (colorSet) => {
    setSelectedColor(colorSet.primary);
    applyTheme(colorSet.primary, colorSet.secondary, colorSet.accent);
    
    // Save to localStorage
    localStorage.setItem('siteTheme', JSON.stringify(colorSet));
    
    toast.success(`Theme changed to ${colorSet.name}!`);
  };

  const handleCustomColorChange = () => {
    if (!customColor || !customColor.match(/^#[0-9A-F]{6}$/i)) {
      toast.error('Please enter a valid hex color (e.g., #ff5733)');
      return;
    }

    const secondary = darkenColor(customColor, 20);
    const accent = lightenColor(customColor, 15);
    
    setSelectedColor(customColor);
    applyTheme(customColor, secondary, accent);
    
    // Save to localStorage
    const customTheme = {
      name: 'Custom',
      primary: customColor,
      secondary: secondary,
      accent: accent
    };
    localStorage.setItem('siteTheme', JSON.stringify(customTheme));
    
    toast.success('Custom theme applied!');
  };

  const resetToDefault = () => {
    const defaultTheme = presetColors[0]; // Red
    setSelectedColor(defaultTheme.primary);
    setCustomColor('');
    applyTheme(defaultTheme.primary, defaultTheme.secondary, defaultTheme.accent);
    localStorage.setItem('siteTheme', JSON.stringify(defaultTheme));
    toast.success('Theme reset to default!');
  };

  return (
    <div className="theme-customizer">
      <div className="theme-header">
        <h2>
          <i className="fas fa-palette"></i>
          Theme Customizer
        </h2>
        <p>Customize your website's primary color theme</p>
      </div>

      <div className="theme-section">
        <h3>Preset Colors</h3>
        <div className="color-grid">
          {presetColors.map((colorSet, index) => (
            <button
              key={index}
              className={`color-option ${selectedColor === colorSet.primary ? 'active' : ''}`}
              onClick={() => handlePresetColorChange(colorSet)}
              style={{ backgroundColor: colorSet.primary }}
              title={colorSet.name}
            >
              {selectedColor === colorSet.primary && (
                <i className="fas fa-check"></i>
              )}
            </button>
          ))}
        </div>
        <div className="color-labels">
          {presetColors.map((colorSet, index) => (
            <span key={index} className="color-label">{colorSet.name}</span>
          ))}
        </div>
      </div>

      <div className="theme-section">
        <h3>Custom Color</h3>
        <div className="custom-color-input">
          <input
            type="text"
            placeholder="#b30101"
            value={customColor}
            onChange={(e) => setCustomColor(e.target.value)}
            maxLength={7}
          />
          <input
            type="color"
            value={customColor || selectedColor}
            onChange={(e) => setCustomColor(e.target.value)}
          />
          <button onClick={handleCustomColorChange} className="apply-btn">
            Apply Custom
          </button>
        </div>
        <p className="hint">Enter a hex color code (e.g., #ff5733) or use the color picker</p>
      </div>

      <div className="theme-section">
        <h3>Preview</h3>
        <div className="theme-preview">
          <div className="preview-item">
            <div className="preview-button" style={{ backgroundColor: selectedColor }}>
              Button
            </div>
          </div>
          <div className="preview-item">
            <div className="preview-badge" style={{ backgroundColor: selectedColor }}>
              Badge
            </div>
          </div>
          <div className="preview-item">
            <div className="preview-icon" style={{ backgroundColor: selectedColor }}>
              <i className="fas fa-home"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="theme-actions">
        <button onClick={resetToDefault} className="reset-btn">
          <i className="fas fa-undo"></i>
          Reset to Default
        </button>
      </div>

      <div className="theme-info">
        <i className="fas fa-info-circle"></i>
        <p>Theme changes are saved automatically and will persist across sessions.</p>
      </div>
    </div>
  );
};

export default ThemeCustomizer;
